import React from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard, BarChart3, Key, Users, CreditCard, Plug, BookOpen, Settings,
  ShieldCheck, Percent, LifeBuoy, Server, Activity, UserCog, LogOut, ScrollText, Mail, LineChart, Gauge, Tag, Coins,
  Rocket, ClipboardCheck, SlidersHorizontal, MapPin, Globe2,
} from 'lucide-react';
import { DOCS_URL } from '@/lib/constants';
import { ROLES, useRoles } from '@/hooks/useRoles';
import { useAuth } from '@/contexts/AuthContext';
import { BrandMark } from '../brand/BrandMark';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  // Omit to show to every authenticated role (matches the route's own
  // RoleGuard). Set this to the exact same roles as that route's RoleGuard,
  // otherwise a user can see and click into a link that just bounces them
  // straight back to their home page with no explanation.
  roles?: string[];
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const SUPER = [ROLES.SUPER_ADMIN];
const ADMIN_ROLES = [ROLES.SUPER_ADMIN, ROLES.ADMIN];
const ADMIN_SUPPORT_ROLES = [...ADMIN_ROLES, ROLES.SUPPORT_STAFF];
const TEAM_ROLES = [ROLES.ENTERPRISE_USER, ROLES.TEAM_MEMBER];
const PROGRAMME_ROLES = [ROLES.PROGRAMME_OFFICER, ...ADMIN_ROLES];

// Running the platform comes first for the people who run it; their own
// account follows. Someone with no admin role sees only "Your account", and
// then without a heading.
const ADMIN_SECTIONS: NavSection[] = [
  {
    label: 'Platform',
    items: [
      { name: 'Platform analytics', href: '/admin/analytics', icon: LineChart, roles: ADMIN_ROLES },
      { name: 'Traffic data', href: '/admin/traffic', icon: Gauge, roles: ADMIN_ROLES },
      { name: 'Platform health', href: '/platform/health', icon: Activity, roles: SUPER },
      { name: 'Services', href: '/admin/services', icon: Server, roles: ADMIN_ROLES },
    ],
  },
  {
    label: 'Customers',
    items: [
      { name: 'Users', href: '/support/users', icon: LifeBuoy, roles: ADMIN_SUPPORT_ROLES },
      { name: 'Credits', href: '/admin/credits', icon: Coins, roles: ADMIN_ROLES },
      { name: 'Coupons', href: '/admin/coupons', icon: Percent, roles: ADMIN_ROLES },
      { name: 'Pricing', href: '/admin/pricing', icon: Tag, roles: ADMIN_ROLES },
    ],
  },
  {
    label: 'Programme review',
    items: [
      { name: 'Applications', href: '/programme', icon: ClipboardCheck, roles: PROGRAMME_ROLES },
      { name: 'Programme settings', href: '/admin/startups', icon: SlidersHorizontal, roles: ADMIN_ROLES },
    ],
  },
  {
    label: 'Security',
    items: [
      { name: 'Audit logs', href: '/admin/audit-logs', icon: ScrollText, roles: ADMIN_ROLES },
      { name: 'Platform admins', href: '/platform/admins', icon: ShieldCheck, roles: SUPER },
      { name: 'Email settings', href: '/admin/email-settings', icon: Mail, roles: SUPER },
      { name: 'Privacy by country', href: '/admin/privacy', icon: Globe2, roles: SUPER },
    ],
  },
];

const ACCOUNT_SECTION: NavSection = {
  label: 'Your account',
  items: [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Usage', href: '/usage', icon: BarChart3 },
    { name: 'Your trips', href: '/trips', icon: MapPin },
    { name: 'API keys', href: '/keys', icon: Key },
    // Team is an organization feature, not a private_user one. Points at
    // /team (not /team/members) since that's the one path both
    // enterprise_user and team_member can actually reach.
    { name: 'Team', href: '/team', icon: Users, roles: TEAM_ROLES },
    { name: 'Roles', href: '/roles', icon: UserCog, roles: TEAM_ROLES },
    { name: 'Billing', href: '/billing', icon: CreditCard, roles: [ROLES.PRIVATE_USER, ROLES.ENTERPRISE_USER] },
    { name: 'Integrations', href: '/integrations', icon: Plug },
    { name: 'Startup programme', href: '/startups/apply', icon: Rocket },
  ],
};

interface SidebarProps {
  onNavigate?: () => void;
}

const itemClass = 'group relative flex h-9 items-center gap-3 rounded-lg px-3 text-[13.5px] font-medium transition-colors';
const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    itemClass,
    isActive
      ? 'bg-accent/10 text-foreground before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-full before:bg-accent'
      : 'text-muted-foreground hover:bg-surface-raised hover:text-foreground',
  );
const iconClass = 'h-[18px] w-[18px] shrink-0';

const Sidebar: React.FC<SidebarProps> = ({ onNavigate }) => {
  const { hasAnyRole } = useRoles();
  const { logout } = useAuth();

  // logout() flips isAuthenticated to false; RoleGuard wrapping AppShell
  // picks that up and redirects to /login on its own; no manual nav needed.
  const handleLogout = () => {
    onNavigate?.();
    logout();
  };

  const visible = [...ADMIN_SECTIONS, ACCOUNT_SECTION]
    .map((section) => ({ ...section, items: section.items.filter((item) => !item.roles || hasAnyRole(...item.roles)) }))
    .filter((section) => section.items.length > 0);
  const showHeadings = visible.length > 1;

  return (
    <aside className="flex h-full w-64 flex-col border-r border-border bg-surface">
      <div className="flex h-16 shrink-0 items-center px-5">
        <BrandMark />
      </div>

      <nav className="scrollbar-thin flex-1 space-y-6 overflow-y-auto px-3 pb-4 pt-2" aria-label="Primary">
        {visible.map((section) => (
          <div key={section.label} role="group" aria-label={section.label} className="space-y-0.5">
            {showHeadings && (
              <p className="px-3 pb-1.5 text-xs font-medium text-muted-foreground/80">{section.label}</p>
            )}
            {section.items.map((item) => (
              <NavLink key={item.href} to={item.href} className={navLinkClass} onClick={onNavigate}>
                <item.icon className={iconClass} />
                <span>{item.name}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="shrink-0 space-y-0.5 border-t border-border p-3">
        <a href={DOCS_URL} target="_blank" rel="noreferrer" className={cn(itemClass, 'text-muted-foreground hover:bg-surface-raised hover:text-foreground')}>
          <BookOpen className={iconClass} />
          <span>Documentation</span>
        </a>
        <NavLink to="/settings" className={navLinkClass} onClick={onNavigate}>
          <Settings className={iconClass} />
          <span>Settings</span>
        </NavLink>
        <button type="button" onClick={handleLogout} className={cn(itemClass, 'w-full text-muted-foreground hover:bg-surface-raised hover:text-foreground')}>
          <LogOut className={iconClass} />
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
